import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import {
	BuildingLibraryIcon,
	CreditCardIcon,
	LockClosedIcon,
	InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Button, Badge, Spinner } from '../../components/ui';
import { providerProfileApi, ApiError } from '../../api';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { PaymentStatus, SubscriptionStatus } from '../../types/payment';

// ---- Map backend subscription status to UI payment status ----
const mapSubscriptionStatus = (backendStatus: SubscriptionStatus): PaymentStatus => {
	switch (backendStatus) {
		case 'NONE':
			return 'not_connected';
		case 'PENDING_VERIFICATION':
			return 'pending_verification';
		default:
			return 'verified';
	}
};

// ---- Progress Stepper ----
const STEPS = [
	{ id: 1, label: 'Account' },
	{ id: 2, label: 'Verify' },
	{ id: 3, label: 'Complete' },
];

const ProgressStepper = ({ currentStep }: { currentStep: number }) => (
	<div className="payment-stepper">
		{STEPS.map((step, index) => (
			<div key={step.id} className="payment-stepper__step">
				{index > 0 && (
					<div
						className={`payment-stepper__connector ${
							step.id <= currentStep ? 'payment-stepper__connector--active' : ''
						}`}
					/>
				)}
				<div
					className={`payment-stepper__circle ${
						step.id < currentStep
							? 'payment-stepper__circle--completed'
							: step.id === currentStep
								? 'payment-stepper__circle--current'
								: 'payment-stepper__circle--upcoming'
					}`}
				>
					{step.id < currentStep ? (
						<CheckCircleIcon className="w-5 h-5" />
					) : (
						<span>{step.id}</span>
					)}
				</div>
				<span
					className={`payment-stepper__label ${
						step.id === currentStep ? 'payment-stepper__label--active' : ''
					}`}
				>
					{step.label}
				</span>
			</div>
		))}
	</div>
);

// ---- Status helpers ----
const getStatusBadge = (status: PaymentStatus, subscriptionStatus?: string) => {
	switch (status) {
		case 'not_connected':
			return <Badge variant="warning">Not Connected</Badge>;
		case 'pending_verification':
			return <Badge variant="info">Pending Verification</Badge>;
		case 'verified':
			return (
				<Badge variant="success">
					{subscriptionStatus === 'ACTIVE' ? 'Active' : 'Verified'}
				</Badge>
			);
	}
};

const getStepFromStatus = (status: PaymentStatus, subscriptionStatus?: string): number => {
	switch (status) {
		case 'not_connected':
			return 1;
		case 'pending_verification':
			return 2;
		case 'verified':
			return subscriptionStatus === 'ACTIVE' ? 4 : 3;
	}
};

// ---- Bank Form Data ----
interface BankFormData {
	accountHolderName: string;
	routingNumber: string;
	accountNumber: string;
	accountType: 'checking' | 'savings';
}

// ---- Not Connected View ----
interface NotConnectedViewProps {
	onConnect: (data: BankFormData) => void;
	isConnecting: boolean;
}

const NotConnectedView = ({ onConnect, isConnecting }: NotConnectedViewProps) => {
	const [accountHolderName, setAccountHolderName] = useState('');
	const [routingNumber, setRoutingNumber] = useState('');
	const [accountNumber, setAccountNumber] = useState('');
	const [accountType, setAccountType] = useState<'checking' | 'savings' | ''>('');

	const handleSubmit = () => {
		if (!accountHolderName.trim()) {
			toast.error('Please enter the account holder name');
			return;
		}
		if (!routingNumber || routingNumber.length !== 9) {
			toast.error('Please enter a valid 9-digit routing number');
			return;
		}
		if (!accountNumber.trim()) {
			toast.error('Please enter the account number');
			return;
		}
		if (!accountType) {
			toast.error('Please select an account type');
			return;
		}
		onConnect({
			accountHolderName: accountHolderName.trim(),
			routingNumber,
			accountNumber: accountNumber.trim(),
			accountType,
		});
	};

	return (
		<div className="payment-setup__bank-form">
			<div className="payment-setup__security-badge">
				<BuildingLibraryIcon className="w-8 h-8" />
			</div>
			<h3 className="payment-setup__title">Connect Your Bank Account</h3>
			<p className="payment-setup__description">
				Add your bank account to start receiving payouts from appointments. Your information
				is securely processed through Stripe.
			</p>

			{/* Account Holder Name */}
			<div className="provider-form__group">
				<label className="provider-form__label">
					Account Holder Name<span className="provider-form__required">*</span>
				</label>
				<input
					type="text"
					className="provider-form__input"
					placeholder="Name on account"
					value={accountHolderName}
					onChange={e => setAccountHolderName(e.target.value)}
					disabled={isConnecting}
				/>
			</div>

			{/* Routing + Account Number */}
			<div className="payment-setup__amounts-row">
				<div className="provider-form__group">
					<label className="provider-form__label">
						Routing Number<span className="provider-form__required">*</span>
					</label>
					<input
						type="text"
						className="provider-form__input"
						placeholder="9 digits"
						maxLength={9}
						value={routingNumber}
						onChange={e => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
						disabled={isConnecting}
					/>
					<span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
						9-digit routing number
					</span>
				</div>
				<div className="provider-form__group">
					<label className="provider-form__label">
						Account Number<span className="provider-form__required">*</span>
					</label>
					<input
						type="text"
						className="provider-form__input"
						placeholder="Account number"
						value={accountNumber}
						onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))}
						disabled={isConnecting}
					/>
				</div>
			</div>

			{/* Account Type */}
			<div className="provider-form__group">
				<label className="provider-form__label">
					Account Type<span className="provider-form__required">*</span>
				</label>
				<select
					className="provider-form__input"
					value={accountType}
					onChange={e => setAccountType(e.target.value as 'checking' | 'savings' | '')}
					disabled={isConnecting}
				>
					<option value="">Select account type</option>
					<option value="checking">Checking</option>
					<option value="savings">Savings</option>
				</select>
			</div>

			{/* Security note */}
			<div className="payment-setup__security-note mb-6">
				<LockClosedIcon className="w-4 h-4" />
				<span>
					Your payment information is encrypted and securely stored. We never share your
					financial data with third parties.
				</span>
			</div>

			{/* Actions */}
			<div className="payment-setup__actions">
				<Button variant="primary" size="lg" onClick={handleSubmit} isLoading={isConnecting}>
					{isConnecting ? 'Connecting...' : 'Save Bank Account'}
				</Button>
			</div>
		</div>
	);
};

// ---- Pending Verification View ----
interface PendingVerificationViewProps {
	onVerify: (amounts: number[]) => void;
	onCancel: () => void;
	isVerifying: boolean;
}

const PendingVerificationView = ({
	onVerify,
	onCancel,
	isVerifying,
}: PendingVerificationViewProps) => {
	const [amount1, setAmount1] = useState('');
	const [amount2, setAmount2] = useState('');

	const handleVerify = () => {
		const a1 = parseInt(amount1);
		const a2 = parseInt(amount2);
		if (!a1 || !a2) {
			toast.error('Please enter both deposit amounts');
			return;
		}
		onVerify([a1, a2]);
	};

	return (
		<div className="payment-setup__verify-form">
			<div className="payment-setup__info-banner">
				<InformationCircleIcon className="w-5 h-5 shrink-0" />
				<p>
					Stripe has sent two small deposits to your bank account. This usually takes 1-2
					business days. Once you see the deposits, enter the amounts below to verify your
					account.
				</p>
			</div>

			<div className="payment-setup__amounts-row">
				<div className="provider-form__group">
					<label className="provider-form__label">
						First Deposit (cents)<span className="provider-form__required">*</span>
					</label>
					<input
						type="number"
						className="provider-form__input"
						placeholder="32"
						min={1}
						max={99}
						step={1}
						value={amount1}
						onChange={e => setAmount1(e.target.value)}
						disabled={isVerifying}
					/>
				</div>
				<div className="provider-form__group">
					<label className="provider-form__label">
						Second Deposit (cents)
						<span className="provider-form__required">*</span>
					</label>
					<input
						type="number"
						className="provider-form__input"
						placeholder="45"
						min={1}
						max={99}
						step={1}
						value={amount2}
						onChange={e => setAmount2(e.target.value)}
						disabled={isVerifying}
					/>
				</div>
			</div>

			<div className="payment-setup__actions">
				<Button variant="primary" size="lg" onClick={handleVerify} isLoading={isVerifying}>
					{isVerifying ? 'Verifying...' : 'Verify Bank Account'}
				</Button>
				<Button variant="secondary" size="lg" onClick={onCancel} disabled={isVerifying}>
					Cancel &amp; Start Over
				</Button>
			</div>
		</div>
	);
};

// ---- Verified View ----
const VerifiedView = ({
	onEdit,
	subscriptionStatus,
}: {
	onEdit: () => void;
	subscriptionStatus: string;
}) => {
	const navigate = useNavigate();

	return (
		<div className="payment-setup__success">
			<div className="payment-setup__success-icon">
				<CheckCircleIcon className="w-10 h-10" />
			</div>
			<h3 className="payment-setup__title">Payment Setup Complete!</h3>
			<p className="payment-setup__description">
				Your bank account has been verified successfully. You can now receive payouts from
				appointments directly to your bank account.
			</p>

			<div className="payment-setup__bank-details">
				<div className="payment-setup__bank-row">
					<span className="payment-setup__bank-label">Payout Schedule</span>
					<span className="payment-setup__bank-value">Monthly</span>
				</div>
				<div className="payment-setup__bank-row">
					<span className="payment-setup__bank-label">Status</span>
					<span className="payment-setup__bank-value text-emerald-600 dark:text-emerald-400">
						{subscriptionStatus === 'ACTIVE' ? 'Active' : 'Verified'}
					</span>
				</div>
			</div>

			<div className="payment-setup__actions payment-setup__actions--center">
				<Button
					variant="primary"
					size="lg"
					onClick={() => navigate('/providers/dashboard')}
				>
					Go to Dashboard
				</Button>
				<Button variant="secondary" size="lg" onClick={onEdit}>
					Edit Bank Account
				</Button>
			</div>
		</div>
	);
};

// ---- Commission Details Card ----
const CommissionDetailsCard = ({ status }: { status: PaymentStatus }) => {
	const { profile } = useProviderProfileStore();

	if (!profile) return null;

	const monthlyFee = profile.baseBbpFee * profile.locationCount;

	return (
		<div className="settings-card">
			<div className="settings-card__header">
				<div className="settings-card__icon">
					<CreditCardIcon className="w-6 h-6" />
				</div>
				<div>
					<h2 className="settings-card__title">Billing Details</h2>
				</div>
			</div>
			<div className="settings-card__body">
				<div className="commission-details__row">
					<span className="commission-details__label">Business Name</span>
					<span className="commission-details__value">{profile.businessName}</span>
				</div>
				<div className="commission-details__row">
					<span className="commission-details__label">Bank Account Status</span>
					<span className="commission-details__value">
						{getStatusBadge(status, profile.subscriptionStatus)}
					</span>
				</div>
				<div className="commission-details__row">
					<span className="commission-details__label">Locations</span>
					<span className="commission-details__value">{profile.locationCount}</span>
				</div>
				<div className="commission-details__row">
					<span className="commission-details__label">Base Fee per Location</span>
					<span className="commission-details__value">${profile.baseBbpFee}/mo</span>
				</div>
				<div className="commission-details__row">
					<span className="commission-details__label">Monthly BBP Fee</span>
					<span className="commission-details__highlight">
						${monthlyFee}/mo{' '}
						<span className="text-xs font-normal text-gray-400 dark:text-gray-500">
							(${profile.baseBbpFee} x {profile.locationCount} location
							{profile.locationCount !== 1 ? 's' : ''})
						</span>
					</span>
				</div>
			</div>
		</div>
	);
};

// ---- Main Page Component ----
const PaymentSetup = () => {
	const { profile, isLoadingProfile, fetchProfile } = useProviderProfileStore();
	const [status, setStatus] = useState<PaymentStatus>('not_connected');
	const [isConnecting, setIsConnecting] = useState(false);
	const [isVerifying, setIsVerifying] = useState(false);

	// Derive status from profile's subscriptionStatus
	useEffect(() => {
		if (profile) {
			setStatus(mapSubscriptionStatus(profile.subscriptionStatus));
		}
	}, [profile]);

	// Handle bank account form submission
	const handleConnect = async (data: BankFormData) => {
		if (!profile) return;

		setIsConnecting(true);
		try {
			// Step 1: Call backend to create SetupIntent
			const response = await providerProfileApi.setupPayment(profile.id);
			const { clientSecret: secret, stripePublishableKey } = response;

			// Step 2: Load Stripe.js with the publishable key from API
			const stripe = await loadStripe(
				stripePublishableKey || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
			);
			if (!stripe) {
				toast.error('Failed to load payment processor');
				return;
			}

			// Step 3: Confirm the SetupIntent directly with bank account details
			const { setupIntent, error: confirmError } = await stripe.confirmUsBankAccountSetup(
				secret,
				{
					payment_method: {
						us_bank_account: {
							routing_number: data.routingNumber,
							account_number: data.accountNumber,
							account_holder_type: 'individual',
							account_type: data.accountType,
						},
						billing_details: {
							name: data.accountHolderName,
						},
					},
				}
			);

			if (confirmError) {
				toast.error(confirmError.message || 'Failed to connect bank account');
				return;
			}

			if (
				setupIntent?.status === 'succeeded' ||
				setupIntent?.next_action?.type === 'verify_with_microdeposits'
			) {
				// Step 4: Notify backend about the Stripe confirmation
				await providerProfileApi.confirmPayment(profile.id);

				// Step 5: Refresh profile to get updated subscriptionStatus
				await fetchProfile();

				if (setupIntent.status === 'succeeded') {
					toast.success('Bank account connected and verified!');
				} else {
					toast.success(
						'Bank account added! Micro-deposits will arrive in 1-2 business days.'
					);
				}
			}
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Failed to set up payment');
		} finally {
			setIsConnecting(false);
		}
	};

	// Handle micro-deposit verification via backend
	const handleVerify = async (amounts: number[]) => {
		if (!profile) return;

		setIsVerifying(true);
		try {
			await providerProfileApi.verifyPayment(profile.id, {
				amounts,
				descriptorCode: '',
			});

			// Refresh profile to get updated subscriptionStatus
			await fetchProfile();
			toast.success('Bank account verified successfully!');
		} catch (err) {
			const apiError = err as ApiError;
			toast.error(apiError.message || 'Verification failed');
		} finally {
			setIsVerifying(false);
		}
	};

	// Handle cancel & start over
	const handleCancel = () => {
		setStatus('not_connected');
	};

	if (isLoadingProfile && !isConnecting && !isVerifying) {
		return (
			<AppLayout>
				<div className="py-16 text-center">
					<Spinner
						size="lg"
						className="mx-auto mb-3 text-purple-600 dark:text-purple-400"
					/>
					<p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
				</div>
			</AppLayout>
		);
	}

	const currentStep = getStepFromStatus(status, profile?.subscriptionStatus);

	return (
		<AppLayout>
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
					Payment Setup
				</h1>
				<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
					Connect your bank account to start receiving payments
				</p>
			</div>

			{/* Progress Stepper */}
			<ProgressStepper currentStep={currentStep} />

			{/* Payment Setup Card */}
			<div className="settings-card mb-6">
				<div className="settings-card__header">
					<div className="settings-card__icon">
						<BuildingLibraryIcon className="w-6 h-6" />
					</div>
					<div className="flex-1">
						<h2 className="settings-card__title">Bank Account</h2>
						<p className="settings-card__subtitle">
							Connect and verify your bank account for payouts
						</p>
					</div>
					{getStatusBadge(status, profile?.subscriptionStatus)}
				</div>
				<div className="settings-card__body">
					{status === 'not_connected' && (
						<NotConnectedView onConnect={handleConnect} isConnecting={isConnecting} />
					)}
					{status === 'pending_verification' && (
						<PendingVerificationView
							onVerify={handleVerify}
							onCancel={handleCancel}
							isVerifying={isVerifying}
						/>
					)}
					{status === 'verified' && (
						<VerifiedView
							onEdit={() => setStatus('not_connected')}
							subscriptionStatus={profile?.subscriptionStatus ?? 'VERIFIED'}
						/>
					)}
				</div>
			</div>

			{/* Commission Details Card */}
			<CommissionDetailsCard status={status} />
		</AppLayout>
	);
};

export default PaymentSetup;
